import { apiClient } from './apiClient';

/**
 * Fetch full play history for the current user and all followed users.
 * Returns:
 *   {
 *     user: { username, plays: [{ id, gameId, score, message, playDate }] },
 *     friends: [{ id, username, plays: [...] }]
 *   }
 */
export const fetchFriendsHistory = async () => {
    return apiClient.get('/friends/history');
};

/**
 * Return a hardcoded dataset matching the /friends/history response shape.
 * Use this during UI development instead of fetchFriendsHistory to avoid
 * needing a live API connection.
 */
export const fetchFriendsHistoryMock = () => {
    // Returns an ISO date string for N days ago at midnight UTC
    const daysAgo = (n) => {
        const d = new Date();
        d.setDate(d.getDate() - n);
        d.setHours(0, 0, 0, 0);
        return d.toISOString();
    };

    let seq = 1;
    const nextId = () => `mock-${seq++}`;

    return {
        user: {
            username: 'demoUser',
            plays: [
                // Wordle (47) – guesses 1-6
                { id: nextId(), gameId: 47, score: '3', message: 'Got it!',      playDate: daysAgo(0) },
                { id: nextId(), gameId: 47, score: '4', message: '',             playDate: daysAgo(1) },
                { id: nextId(), gameId: 47, score: '3', message: '',             playDate: daysAgo(2) },
                { id: nextId(), gameId: 47, score: '5', message: 'Hard one',     playDate: daysAgo(3) },
                { id: nextId(), gameId: 47, score: '2', message: 'Lucky!',       playDate: daysAgo(4) },
                { id: nextId(), gameId: 47, score: '4', message: '',             playDate: daysAgo(5) },
                { id: nextId(), gameId: 47, score: '3', message: '',             playDate: daysAgo(6) },
                { id: nextId(), gameId: 47, score: '4', message: '',             playDate: daysAgo(7) },
                { id: nextId(), gameId: 47, score: '3', message: '',             playDate: daysAgo(8) },
                { id: nextId(), gameId: 47, score: '6', message: 'Phew!',        playDate: daysAgo(9) },
                // The Mini (1) – time as "M:SS"
                { id: nextId(), gameId: 1,  score: '1:23', message: '',              playDate: daysAgo(0) },
                { id: nextId(), gameId: 1,  score: '2:10', message: '',              playDate: daysAgo(1) },
                { id: nextId(), gameId: 1,  score: '0:58', message: 'New record!',   playDate: daysAgo(2) },
                { id: nextId(), gameId: 1,  score: '1:45', message: '',              playDate: daysAgo(3) },
                { id: nextId(), gameId: 1,  score: '3:12', message: 'Tricky',        playDate: daysAgo(4) },
                { id: nextId(), gameId: 1,  score: '1:30', message: '',              playDate: daysAgo(5) },
                { id: nextId(), gameId: 1,  score: '2:04', message: '',              playDate: daysAgo(6) },
                // Connections (6) – mistakes remaining (0-3, higher is better)
                { id: nextId(), gameId: 6,  score: '3', message: '',             playDate: daysAgo(0) },
                { id: nextId(), gameId: 6,  score: '2', message: '',             playDate: daysAgo(1) },
                { id: nextId(), gameId: 6,  score: '3', message: '',             playDate: daysAgo(2) },
                { id: nextId(), gameId: 6,  score: '1', message: 'Struggled',    playDate: daysAgo(3) },
                { id: nextId(), gameId: 6,  score: '3', message: '',             playDate: daysAgo(4) },
                { id: nextId(), gameId: 6,  score: '4', message: 'Perfect!',     playDate: daysAgo(5) },
                { id: nextId(), gameId: 6,  score: '2', message: '',             playDate: daysAgo(6) },
                { id: nextId(), gameId: 6,  score: '0', message: 'Brutal',       playDate: daysAgo(7) },
                // Strands (9) – hints used (lower is better)
                { id: nextId(), gameId: 9,  score: '0', message: 'No hints!',    playDate: daysAgo(0) },
                { id: nextId(), gameId: 9,  score: '1', message: '',             playDate: daysAgo(1) },
                { id: nextId(), gameId: 9,  score: '0', message: '',             playDate: daysAgo(2) },
                { id: nextId(), gameId: 9,  score: '2', message: '',             playDate: daysAgo(3) },
                { id: nextId(), gameId: 9,  score: '0', message: '',             playDate: daysAgo(5) },
                { id: nextId(), gameId: 9,  score: '1', message: '',             playDate: daysAgo(7) },
                // Waffle (59) – stars earned (1-5)
                { id: nextId(), gameId: 59, score: '5', message: '5 stars!',     playDate: daysAgo(0) },
                { id: nextId(), gameId: 59, score: '4', message: '',             playDate: daysAgo(1) },
                { id: nextId(), gameId: 59, score: '5', message: '',             playDate: daysAgo(2) },
                { id: nextId(), gameId: 59, score: '3', message: 'Off day',      playDate: daysAgo(3) },
                { id: nextId(), gameId: 59, score: '5', message: '',             playDate: daysAgo(4) },
                { id: nextId(), gameId: 59, score: '4', message: '',             playDate: daysAgo(5) },
                // Raddle (11) – percentage score
                { id: nextId(), gameId: 11, score: '87', message: '',            playDate: daysAgo(0) },
                { id: nextId(), gameId: 11, score: '92', message: '',            playDate: daysAgo(2) },
                { id: nextId(), gameId: 11, score: '78', message: '',            playDate: daysAgo(4) },
                { id: nextId(), gameId: 11, score: '95', message: 'Best yet',    playDate: daysAgo(6) },
                { id: nextId(), gameId: 11, score: '83', message: '',            playDate: daysAgo(8) },
                // Hocus Focus (80) – stars (1-3)
                { id: nextId(), gameId: 80, score: '3', message: '',             playDate: daysAgo(0) },
                { id: nextId(), gameId: 80, score: '2', message: '',             playDate: daysAgo(1) },
                { id: nextId(), gameId: 80, score: '3', message: '',             playDate: daysAgo(2) },
                { id: nextId(), gameId: 80, score: '1', message: 'Confusing',    playDate: daysAgo(3) },
                { id: nextId(), gameId: 80, score: '3', message: '',             playDate: daysAgo(4) },
                // One Word Search (41) – time
                { id: nextId(), gameId: 41, score: '4:12', message: '',          playDate: daysAgo(0) },
                { id: nextId(), gameId: 41, score: '3:55', message: '',          playDate: daysAgo(1) },
                { id: nextId(), gameId: 41, score: '5:01', message: '',          playDate: daysAgo(2) },
            ],
        },
        friends: [
            {
                id: 'mock-friend-1',
                username: 'wordNerd42',
                plays: [
                    // Wordle (47)
                    { id: nextId(), gameId: 47, score: '2', message: 'Easy',         playDate: daysAgo(0) },
                    { id: nextId(), gameId: 47, score: '3', message: '',             playDate: daysAgo(1) },
                    { id: nextId(), gameId: 47, score: '4', message: '',             playDate: daysAgo(2) },
                    { id: nextId(), gameId: 47, score: '3', message: '',             playDate: daysAgo(3) },
                    { id: nextId(), gameId: 47, score: '2', message: '',             playDate: daysAgo(4) },
                    { id: nextId(), gameId: 47, score: '5', message: 'Phew',         playDate: daysAgo(5) },
                    { id: nextId(), gameId: 47, score: '3', message: '',             playDate: daysAgo(6) },
                    { id: nextId(), gameId: 47, score: '2', message: '',             playDate: daysAgo(7) },
                    // The Mini (1)
                    { id: nextId(), gameId: 1,  score: '0:47', message: 'PB!',       playDate: daysAgo(0) },
                    { id: nextId(), gameId: 1,  score: '1:02', message: '',          playDate: daysAgo(1) },
                    { id: nextId(), gameId: 1,  score: '1:18', message: '',          playDate: daysAgo(2) },
                    { id: nextId(), gameId: 1,  score: '2:33', message: '',          playDate: daysAgo(4) },
                    { id: nextId(), gameId: 1,  score: '0:52', message: '',          playDate: daysAgo(5) },
                    // Connections (6)
                    { id: nextId(), gameId: 6,  score: '4', message: 'Perfect!',     playDate: daysAgo(0) },
                    { id: nextId(), gameId: 6,  score: '3', message: '',             playDate: daysAgo(1) },
                    { id: nextId(), gameId: 6,  score: '2', message: '',             playDate: daysAgo(2) },
                    { id: nextId(), gameId: 6,  score: '4', message: '',             playDate: daysAgo(3) },
                    { id: nextId(), gameId: 6,  score: '1', message: '',             playDate: daysAgo(5) },
                    { id: nextId(), gameId: 6,  score: '4', message: '',             playDate: daysAgo(6) },
                    // Spelling Bee (32) – total points
                    { id: nextId(), gameId: 32, score: '150', message: 'Queen Bee!', playDate: daysAgo(0) },
                    { id: nextId(), gameId: 32, score: '92',  message: '',           playDate: daysAgo(1) },
                    { id: nextId(), gameId: 32, score: '134', message: '',           playDate: daysAgo(2) },
                    { id: nextId(), gameId: 32, score: '78',  message: '',           playDate: daysAgo(3) },
                    { id: nextId(), gameId: 32, score: '120', message: '',           playDate: daysAgo(4) },
                    // Strands (9)
                    { id: nextId(), gameId: 9,  score: '1', message: '',             playDate: daysAgo(0) },
                    { id: nextId(), gameId: 9,  score: '0', message: 'Clean',        playDate: daysAgo(2) },
                    { id: nextId(), gameId: 9,  score: '1', message: '',             playDate: daysAgo(4) },
                ],
            },
            {
                id: 'mock-friend-2',
                username: 'puzzlePro',
                plays: [
                    // Wordle (47)
                    { id: nextId(), gameId: 47, score: '4', message: '',             playDate: daysAgo(0) },
                    { id: nextId(), gameId: 47, score: '3', message: '',             playDate: daysAgo(1) },
                    { id: nextId(), gameId: 47, score: '6', message: 'Just made it!',playDate: daysAgo(2) },
                    { id: nextId(), gameId: 47, score: '4', message: '',             playDate: daysAgo(3) },
                    { id: nextId(), gameId: 47, score: '3', message: '',             playDate: daysAgo(5) },
                    // Connections (6)
                    { id: nextId(), gameId: 6,  score: '3', message: '',             playDate: daysAgo(0) },
                    { id: nextId(), gameId: 6,  score: '4', message: 'Nailed it',    playDate: daysAgo(1) },
                    { id: nextId(), gameId: 6,  score: '2', message: '',             playDate: daysAgo(3) },
                    { id: nextId(), gameId: 6,  score: '3', message: '',             playDate: daysAgo(5) },
                    // Raddle (11)
                    { id: nextId(), gameId: 11, score: '91', message: '',            playDate: daysAgo(0) },
                    { id: nextId(), gameId: 11, score: '73', message: '',            playDate: daysAgo(1) },
                    { id: nextId(), gameId: 11, score: '88', message: '',            playDate: daysAgo(3) },
                    { id: nextId(), gameId: 11, score: '96', message: 'Best ever',   playDate: daysAgo(7) },
                    // Waffle (59)
                    { id: nextId(), gameId: 59, score: '5', message: '',             playDate: daysAgo(0) },
                    { id: nextId(), gameId: 59, score: '5', message: '',             playDate: daysAgo(1) },
                    { id: nextId(), gameId: 59, score: '4', message: '',             playDate: daysAgo(2) },
                    { id: nextId(), gameId: 59, score: '5', message: '',             playDate: daysAgo(3) },
                    { id: nextId(), gameId: 59, score: '5', message: 'Always waffle',playDate: daysAgo(4) },
                    // Hocus Focus (80)
                    { id: nextId(), gameId: 80, score: '3', message: '',             playDate: daysAgo(0) },
                    { id: nextId(), gameId: 80, score: '3', message: 'Easy today',   playDate: daysAgo(1) },
                    { id: nextId(), gameId: 80, score: '2', message: '',             playDate: daysAgo(3) },
                    // Strands (9)
                    { id: nextId(), gameId: 9,  score: '1', message: '',             playDate: daysAgo(0) },
                    { id: nextId(), gameId: 9,  score: '0', message: '',             playDate: daysAgo(2) },
                    { id: nextId(), gameId: 9,  score: '2', message: '',             playDate: daysAgo(4) },
                    // One Word Search (41)
                    { id: nextId(), gameId: 41, score: '3:44', message: '',          playDate: daysAgo(0) },
                    { id: nextId(), gameId: 41, score: '2:58', message: 'Fast!',     playDate: daysAgo(1) },
                    { id: nextId(), gameId: 41, score: '4:20', message: '',          playDate: daysAgo(3) },
                ],
            },
        ],
    };
};
